import {useElements, useStripe} from '@stripe/react-stripe-js'
import {Breadcrumb, Breadcrumbs, Form} from 'react-aria-components'

import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import foodGrubhubCartDb from '@/lib/food-grubhub/foodGrubhubCartDb'
import {createFoodGrubhubPaymentIntent} from '@/lib/food-grubhub/foodGrubhubPaymentService'
import useFoodGrubhubCartSummary from '@/lib/food-grubhub/hooks/useFoodGrubhubCartSummary'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import {
  useGetDropOffNote,
  useGetGrubhubUserAddress,
  useGrubhubUserAddress,
} from '@/lib/food-grubhub/store'
import HeadTags from '@/lib/head/HeadTags'
import {useCountry} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import type {ContactInfoControl} from '@/lib/payment/components/ContactInfo'
import ContactInfo from '@/lib/payment/components/ContactInfo'
import type {Contact} from '@/lib/payment/components/model'
import PaymentMethod from '@/lib/payment/components/PaymentMethod'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import useCustomerSession from '@/lib/payment/hooks/useCustomerSession'
import PaymentProvider, {useSetElementsOptions} from '@/lib/payment/PaymentProvider'
import {getRoundUp} from '@/lib/payment/store'
import {RESOLVED_ERRORS, StripeElement} from '@/lib/payment/stripeErrors'
import toStripeAmountNumber from '@/lib/payment/utils/toStripeAmountNumber'
import {
  FoodGrubhubCartRoute,
  FoodGrubhubPaymentRoute,
  FoodGrubhubPaymentSuccessRoute,
  FoodGrubhubRoute,
} from '@/routeRegistry'
import {buildStringForCalibratedError, isCartCalibratedError} from '@/services/cartService'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import RACLink from '@/utils/router/RACLink'
import useSearch from '@/utils/router/useSearch'

import ContactInfo2, {type Contact2, type ContactInfo2Control} from './components/ContactInfo2'
import FoodGrubhubPaymentSummary from './components/FoodGrubhubPaymentSummary'
import style from './FoodGrubhubPayment.module.scss'

const Payment = memo(function Payment() {
  const router = useRouter()
  const store = useStore()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const [country] = useCountry()
  const latestCountry = useLatest(country)
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const [isRewardsApplied, setIsRewardsApplied] = useSearch(
    FoodGrubhubPaymentRoute.id,
    'applyRewards',
  )
  const [method, setMethod] = useSearch(FoodGrubhubPaymentRoute.id, 'method', {
    replace: false,
    resetScroll: true,
  })
  const [coupon, setCoupon] = useSearch(FoodGrubhubPaymentRoute.id, 'coupon')
  const latestMethod = useLatest(method)

  const [loading, setLoading] = useState(false)
  const isAuthenticated = useIsAuthenticated()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const {data: customerSession} = useCustomerSession()
  const stripe = useStripe()
  const elements = useElements()
  const latestIsRewardsApplied = useLatest(isRewardsApplied)
  const contactInfoRef = useRef<ContactInfoControl>(null)
  const contactInfo2Ref = useRef<ContactInfo2Control>(null)
  const paymentMethodId = useId()
  const getDropOffNote = useGetDropOffNote()
  const {data: authResponse} = useGrubhubGetAuth()
  const latestAccessToken = useLatest(authResponse?.session_handle.access_token)
  const getUserAddress = useGetGrubhubUserAddress(store)

  const setElementsOptions = useSetElementsOptions()

  const cartSummary = useFoodGrubhubCartSummary(isRewardsApplied, coupon, setCoupon)
  const latestPartnerCartId = useLatest(cartSummary.cart?.moreInfo?.partnerCartId)
  const latestCoupon = useLatest(coupon)

  useEffect(
    function updateElementsOptions() {
      setElementsOptions(options => {
        options.mode = 'payment'
        options.setupFutureUsage = 'off_session' // vs on?
        if (customerSession) {
          options.customerSessionClientSecret = customerSession
        }
        options.amount = toStripeAmountNumber(cartSummary.total)
      })
    },
    [customerSession, cartSummary.total, setElementsOptions],
  )

  const handleSubmit = useCallback(
    async (contact: Contact & Contact2, shouldShiftFocus = false) => {
      if (!stripe || !elements) {
        return
      }

      setLoading(true)

      try {
        const {error: submitError} = await elements.submit()
        if (submitError) {
          console.error(submitError)

          const resolveTo = RESOLVED_ERRORS.find(error =>
            [submitError.code, submitError.decline_code].includes(error.code),
          )

          if (resolveTo?.belongsTo === StripeElement.Address) {
            // move back to step 1
            void setMethod(false).then(() => {
              elements.getElement('address')?.focus()
            })
          } else if (resolveTo?.belongsTo === StripeElement.Payment) {
            // move to step 2
            void setMethod(true).then(() => {
              elements.getElement('payment')?.focus()
            })
          } else {
            // move back to step 1
            toastErrorMessage('Unexpected error, please start over. Sorry for the inconvenience!')
            void setMethod(false).then(() => {
              elements.getElement('address')?.focus()
            })
          }

          return
        }

        if (!latestMethod.current) {
          void setMethod(true).then(() => {
            if (!shouldShiftFocus) return
            document.getElementById(paymentMethodId)?.focus()
          })
          return
        }

        const {error: confirmationError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              allow_redisplay: 'always',
            },
          },
        })

        if (confirmationError) {
          console.error(confirmationError)
          toastErrorMessage(confirmationError)
          return
        }

        const returnUrl = router.buildLocation({
          to: FoodGrubhubCartRoute.fullPath,
        }).href

        const userAddress = getUserAddress()

        // Create the PaymentIntent
        const {clientSecret, order, paymentIntentStatus} = await createFoodGrubhubPaymentIntent({
          foodToken: latestAccessToken.current ?? '',
          isAuthenticated: latestIsAuthenticated.current,
          confirmationTokenId: confirmationToken.id,
          applyRewards: latestIsRewardsApplied.current,
          returnUrl,
          queryClient,
          locale: latestLocale.current,
          email: contact.email,
          name: contact.name,
          phone: contact.phone,
          line1: `${userAddress.address_detail.street_number} ${userAddress.address_detail.route}`,
          line2: '',
          city: userAddress.address_detail.locality,
          state: userAddress.address_detail.administrative_area_level_1,
          postalCode: userAddress.address_detail.postal_code,
          coupon: latestCoupon.current,
          roundUp: getRoundUp(store),
          longitude: userAddress.longitude ?? 0,
          latitude: userAddress.latitude ?? 0,
          notes: getDropOffNote(),
          partnerCartId: latestPartnerCartId.current,
        })

        if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
          if (paymentIntentStatus === 'requires_action') {
            const {error: nextActionError} = await stripe.handleNextAction({clientSecret})

            if (nextActionError) {
              // eslint-disable-next-line @typescript-eslint/only-throw-error -- its okay
              throw nextActionError
            }
          }
        }

        foodGrubhubCartDb.clearFoodGrubhubCart(latestCountry.current)

        await router.navigate({
          to: FoodGrubhubPaymentSuccessRoute.fullPath,
          search: {
            id: order.id,
          },
        })
      } catch (e) {
        if (isCartCalibratedError(e)) {
          toastErrorMessage(buildStringForCalibratedError(e))
        } else {
          console.error(e)
          toastErrorMessage(e)
        }
        return
      } finally {
        setLoading(false)
      }
    },
    [
      stripe,
      elements,
      router,
      getUserAddress,
      queryClient,
      store,
      setMethod,
      paymentMethodId,
      getDropOffNote,
    ],
  )

  const formRef = useRef<HTMLFormElement>(null)

  const validateContactInfo = useCallback(async () => {
    const contact1 = await contactInfoRef.current?.submit()
    if (!contact1) return

    const contact2 = await contactInfo2Ref.current?.submit()
    if (!contact2) return

    return {
      email: contact1.email,
      name: contact2.name,
      phone: contact2.phone,
    }
  }, [contactInfoRef, contactInfo2Ref])

  // ---------------------------------------------------------------------------

  const latestHandleSubmit = useLatest(handleSubmit)

  const onSubmitButtonPress = useCallback(
    async (shouldShiftFocus = false) => {
      const contact = await validateContactInfo()
      if (!contact) return
      void latestHandleSubmit.current(contact, shouldShiftFocus)
    },
    [validateContactInfo],
  )

  // ---------------------------------------------------------------------------

  const title = method ? 'Payment method' : 'Checkout'

  const [userAddress] = useGrubhubUserAddress()

  const userAddressString = userAddress.address ?? 'None'

  return (
    <>
      <HeadTags title={title} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar
          transparentBg
          withoutLogoOnDesktop
          variant='light'
          className={style.Navbar}
          haveCart={false}
        />
        <main className={style.CartPage} {...skipTargetProps('main-content')}>
          <div className={style.CartContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={FoodGrubhubRoute.fullPath}>
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodGrubhubCartRoute.fullPath}
                    search={{
                      coupon,
                      applyRewards: isRewardsApplied,
                    }}
                    activeOptions={{
                      exact: true,
                      includeSearch: true,
                    }}
                  >
                    Food Cart
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodGrubhubPaymentRoute.fullPath}
                    search={{method: false, applyRewards: isRewardsApplied, coupon}}
                    activeOptions={{
                      exact: true,
                      includeSearch: true,
                    }}
                  >
                    Checkout
                  </RACLink>
                </Breadcrumb>
                {method && (
                  <Breadcrumb className={style.Breadcrumb}>
                    <RACLink
                      className={style.Link}
                      to={FoodGrubhubPaymentRoute.fullPath}
                      search={{method: true, applyRewards: isRewardsApplied}}
                      activeOptions={{
                        exact: true,
                        includeSearch: true,
                      }}
                    >
                      Payment method
                    </RACLink>
                  </Breadcrumb>
                )}
              </Breadcrumbs>
            </div>
            <h1 className={style.Heading}>{title}</h1>
            <div className={style.DeliveryInfo}>
              Delivery to: <span>{userAddressString}</span>
            </div>
            <div className={style.Cart}>
              <div className={style.CartItems}>
                <VisuallyHidden
                  as='div'
                  isVisible={!method}
                  aria-hidden={method}
                  tabIndex={method ? -1 : undefined}
                  inert={method}
                  role={method ? 'none' : undefined}
                >
                  <>
                    <h2>Contact info</h2>
                    <ContactInfo ref={contactInfoRef} className={style.Wrapper} />
                  </>
                </VisuallyHidden>
                <Form
                  ref={formRef}
                  onSubmit={e => {
                    e.preventDefault()
                    void onSubmitButtonPress()
                  }}
                >
                  <VisuallyHidden
                    as='div'
                    isVisible={!method}
                    aria-hidden={method}
                    tabIndex={method ? -1 : undefined}
                    inert={method}
                    role={method ? 'none' : undefined}
                  >
                    <h2>Shipping information</h2>
                    <ContactInfo2 ref={contactInfo2Ref} className={style.Wrapper} />
                  </VisuallyHidden>
                  {method && (
                    <>
                      <h2 id={paymentMethodId} tabIndex={-1}>
                        Payment method
                      </h2>
                      <div className={style.Wrapper}>
                        <PaymentMethod total={cartSummary.total} />
                      </div>
                    </>
                  )}
                  <VisuallyHidden
                    as={PrimaryButton}
                    className={style.PayBtn2}
                    onPress={() => {
                      void onSubmitButtonPress(true)
                    }}
                    aria-label='Pay with Card (Guaranteed safe & secure, powered by Stripe)'
                  >
                    {(() => {
                      if (!method) return 'Next step'
                      return 'Pay with Card'
                    })()}
                  </VisuallyHidden>
                </Form>
              </div>
              {!isMobile && (
                <FoodGrubhubPaymentSummary
                  onSubmit={onSubmitButtonPress}
                  isPending={loading}
                  isRewardsApplied={isRewardsApplied}
                  setIsRewardsApplied={setIsRewardsApplied}
                  cartSummary={cartSummary}
                  coupon={coupon}
                  validateContactInfo={validateContactInfo}
                />
              )}
            </div>
          </div>
        </main>
        {isMobile && (
          <FoodGrubhubPaymentSummary
            onSubmit={onSubmitButtonPress}
            isPending={loading}
            isRewardsApplied={isRewardsApplied}
            setIsRewardsApplied={setIsRewardsApplied}
            cartSummary={cartSummary}
            coupon={coupon}
            validateContactInfo={validateContactInfo}
          />
        )}
        <Footer />
      </div>
    </>
  )
})

const PaymentWrapped = memo(function PaymentWrapped() {
  return (
    <PaymentProvider>
      <Payment />
    </PaymentProvider>
  )
})

export default PaymentWrapped
